import React, { useState, useEffect, useMemo } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { facilitiesService } from "../api/services/facilities/facilitiesService";
import {
  FacilityCategory,
  CreateFacilityCategoryRequest,
} from "../api/services/facilities/type";
import PrimaryButton from "../components/ui/PrimaryButton";
import toast from "react-hot-toast";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";

export const FacilitiesCategories: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [categories, setCategories] = useState<FacilityCategory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<FacilityCategory | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<FacilityCategory | null>(
    null,
  );
  const [formData, setFormData] = useState<CreateFacilityCategoryRequest>({
    name: "",
    description: "",
  });
  const itemsPerPage = 10;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch categories
  useEffect(() => {
    fetchCategories();
  }, [currentPage, debouncedSearchTerm]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await facilitiesService.getCategories({
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        name: debouncedSearchTerm || undefined,
      });
      console.log({ response });
      setCategories(response.items || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load facilities categories");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / itemsPerPage) || 1;
  }, [totalCount]);

  const handleCreateOrUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setLoading(true);
    try {
      if (editingCategory) {
        await facilitiesService.updateCategory(editingCategory.id, formData);
        toast.success("Category updated successfully");
      } else {
        await facilitiesService.createCategory(formData);
        toast.success("Category created successfully");
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: "", description: "" });
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error(
        editingCategory
          ? "Failed to update category"
          : "Failed to create category",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCategory) return;

    setLoading(true);
    try {
      await facilitiesService.deleteCategory(deleteCategory.id);
      toast.success("Category deleted successfully");
      setDeleteCategory(null);
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (category: FacilityCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "" });
    setShowModal(true);
  };

  console.log("BIbash", { categories });

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">
            Facilities Categories
          </h1>
          <p className="text-slate-400 mt-1">
            Manage facility categories for player services
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search categories..."
              />
            </div>
            <PrimaryButton onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </PrimaryButton>
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    {searchTerm
                      ? "No categories found matching your search"
                      : "No categories created yet"}
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr
                    key={category.id}
                    className="hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-white font-medium">
                        {category.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">
                        {category.description || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-400 text-sm">
                        {new Date(category.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEditModal(category)}
                        className="text-blue-400 hover:text-blue-300 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteCategory(category)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Results info */}
        {totalCount > 0 && (
          <div className="mt-4 text-sm text-slate-400 text-center">
            Showing {Math.min(itemsPerPage * currentPage, totalCount)} of{" "}
            {totalCount} categories
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingCategory ? "Edit Category" : "Create New Category"}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Accommodation, Transportation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Optional description..."
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingCategory(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Saving..." : editingCategory ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteCategory && (
        <ConfirmDialog
          open={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteCategory(null)}
          title="Delete Category"
          description={`Are you sure you want to delete "${deleteCategory.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};
